import { PropsWithChildren, useEffect } from "react"
import { CapacitorUpdater } from "@capgo/capacitor-updater"
import { datadogLogs } from "@datadog/browser-logs"
import { Capacitor } from "@capacitor/core"
import { App } from "@capacitor/app"
import { useNavigate } from "react-router-dom"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { saveLoginActivityOnNativeApp } from "@/utils/nativeAppActivity"
import { useAppUpdateCheck } from "@/hooks/useAppUpdateCheck"
import { UpdateAvailableDialog } from "@/components/Dialog/UpdateAvailableDialog"

const useDeepLink = () => {
    const navigate = useNavigate()

    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return

        const listener = App.addListener("appUrlOpen", event => {
            try {
                const url = new URL(event.url)
                navigate(url.pathname + url.search)
            } catch (e) {
                console.error("Failed to parse deep link URL:", event.url, e)
            }
        })

        return () => {
            listener.then(l => l.remove())
        }
    }, [navigate])
}

const useLoginActivity = () => {
    const { token, authenticated } = useAuth()

    useEffect(() => {
        if (authenticated && token && Capacitor.isNativePlatform()) {
            checkAndSaveActivityWithTimeLimit(token)
        }
    }, [authenticated, token])
}

const checkAndSaveActivityWithTimeLimit = async (token: string) => {
    const lastSaved = sessionStorage.getItem("last_activity_saved")
    const now = Date.now()
    const timeLimit = 24 * 60 * 60 * 1000 // 24時間

    if (!lastSaved || now - parseInt(lastSaved) > timeLimit) {
        try {
            await saveLoginActivityOnNativeApp(token)
            sessionStorage.setItem("last_activity_saved", now.toString())
        } catch (error) {
            console.error("Failed to save login activity:", error)
        }
    }
}

const useDatadogUser = () => {
    const { token, authenticated } = useAuth()

    useEffect(() => {
        if (!authenticated || !token) {
            datadogLogs.clearUser()
            return
        }
        try {
            const { userId, companyId } = JSON.parse(atob(token.split(".")[1]))
            CapacitorUpdater.getDeviceId()
                .then(({ deviceId }) => {
                    datadogLogs.setUser({
                        id: userId,
                        cleanerId: userId,
                        companyId,
                        capgoDeviceId: deviceId
                    })
                })
                .catch(() => {
                    datadogLogs.setUser({
                        id: userId,
                        cleanerId: userId,
                        companyId
                    })
                })
        } catch {
            // トークンのパースに失敗した場合は何もしない
        }
    }, [authenticated, token])
}

export const AppInitializer = ({ children }: PropsWithChildren) => {
    useDeepLink()
    useLoginActivity()
    useDatadogUser()
    const updateCheck = useAppUpdateCheck()
    return (
        <>
            {children}
            <UpdateAvailableDialog
                open={updateCheck.open}
                availableVersion={updateCheck.availableVersion}
                onUpdate={updateCheck.onUpdate}
                onLater={updateCheck.onLater}
            />
        </>
    )
}
